import { useCallback, useMemo, useState } from 'react'
import { EyeOff, FileUp, Pencil, Plus, RadioTower, RefreshCw, Search, Trash2, UserRound } from 'lucide-react'
import {
  Avatar,
  Badge,
  Button,
  ButtonLink,
  ConfirmDialog,
  EmptyState,
  IconButton,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tabs,
  TextField,
  useToast,
} from '@/components/play'
import { ContentSkeleton } from '@/components/loading/Skeleton'
import { formatAge } from '@/components/map/camera-sphere/formatAge'
import {
  apiErrors,
  assignTracker,
  loadAthletes,
  loadCameras,
  loadRaces,
  loadTrackers,
  loadTrackingSources,
  removeAthlete,
} from '@/lib/athletes/api'
import type { AthleteRecord, Camera, Race, TrackerRecord, TrackingSource } from '@/lib/athletes/types'
import { useRefresh } from '@/lib/athletes/useRefresh'
import { workspaceError } from '@/lib/workspace'
import { AthleteDialog } from './AthleteDialog'
import { AthletePreview } from './AthletePreview'
import { AthleteImportDialog } from './AthleteImportDialog'
import { RaceLabel, raceLooks } from './RaceLabel'
import { TrackerAssignDialog } from './TrackerAssignDialog'
import { TrackersTable } from './TrackersTable'

/** Rows shown at a time; a roster can run to thousands. */
const PAGE = 200

interface Props {
  eventId: string
}

interface Loaded {
  athletes: AthleteRecord[]
  races: Race[]
  sources: TrackingSource[]
  trackers: TrackerRecord[]
  cameras: Camera[]
}

type View = 'athletes' | 'trackers'

/** Trackers no one carries first, as they wait on the host; then the latest seen. */
function trackerOrder(a: TrackerRecord, b: TrackerRecord): number {
  const carried = Number(Boolean(a.athlete_id || a.stream_id)) - Number(Boolean(b.athlete_id || b.stream_id))
  if (carried) return carried
  const seen = (b.location?.recorded_at ?? '').localeCompare(a.location?.recorded_at ?? '')
  return seen || a.external_ref.localeCompare(b.external_ref)
}

/**
 * An event's athletes, its workspace's Athletes view (docs/features/athletes-and-trackers.md, Host): the roster to add
 * to, edit, import into, search and preview as viewers see each athlete, and beside it every tracker and who carries
 * it, to assign, reassign or unassign. Tracking providers are connected in the event's settings; it reads which are,
 * so a tracker id can be saved.
 */
export function EventAthletesPanel({ eventId }: Props) {
  const { toast } = useToast()
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState('')
  const [now, setNow] = useState(() => Date.now())
  const [view, setView] = useState<View>('athletes')
  const [query, setQuery] = useState('')
  const [shown, setShown] = useState(PAGE)
  const [editing, setEditing] = useState<{ athlete: AthleteRecord | null } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  // The tracker being assigned, and the bib of whoever carries it now.
  const [assigning, setAssigning] = useState<{ tracker: TrackerRecord; bib: string | null } | null>(null)
  // The target outlives `removalOpen` so the confirmation keeps its title while it closes.
  const [removal, setRemoval] = useState<AthleteRecord | null>(null)
  const [removalOpen, setRemovalOpen] = useState(false)
  const [removalError, setRemovalError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  const load = useCallback(async () => {
    setRefreshing(true)
    try {
      const [athletes, races, sources, trackers, cameras] = await Promise.all([
        loadAthletes(eventId),
        loadRaces(eventId),
        loadTrackingSources(eventId),
        loadTrackers(eventId),
        loadCameras(eventId),
      ])
      setData({ athletes, races, sources, trackers: trackers.sort(trackerOrder), cameras })
      setError('')
    } catch (failure) {
      setError(workspaceError(failure, 'Your athletes couldn’t load. Try again.'))
    } finally {
      setNow(Date.now())
      setRefreshing(false)
    }
  }, [eventId])

  useRefresh(load)

  const looks = useMemo(() => raceLooks(data?.races ?? []), [data])
  const athletesById = useMemo(() => new Map((data?.athletes ?? []).map((athlete) => [athlete.id, athlete])), [data])
  const camerasById = useMemo(() => new Map((data?.cameras ?? []).map((camera) => [camera.id, camera])), [data])
  const text = query.trim().toLowerCase()
  const matching = useMemo(() => {
    const athletes = data?.athletes ?? []
    if (!text) return athletes
    return athletes.filter((athlete) => athlete.name.toLowerCase().includes(text) || athlete.bib?.toLowerCase() === text || athlete.trackers.some((tracker) => tracker.external_ref.toLowerCase() === text))
  }, [data, text])
  // A tracker by its id or provider's name, or by who carries it: their name or bib, or the camera's name.
  const matchingTrackers = useMemo(() => {
    const trackers = data?.trackers ?? []
    if (!text) return trackers
    return trackers.filter((tracker) => {
      const athlete = tracker.athlete_id ? athletesById.get(tracker.athlete_id) : undefined
      const camera = tracker.stream_id ? camerasById.get(tracker.stream_id) : undefined
      return (
        tracker.external_ref.toLowerCase().includes(text) ||
        tracker.label?.toLowerCase().includes(text) ||
        athlete?.name.toLowerCase().includes(text) ||
        athlete?.bib?.toLowerCase() === text ||
        camera?.name.toLowerCase().includes(text)
      )
    })
  }, [data, text, athletesById, camerasById])

  function assign(tracker: TrackerRecord) {
    setAssigning({ tracker, bib: (tracker.athlete_id && athletesById.get(tracker.athlete_id)?.bib) || null })
  }

  // Unassigned at once, with Undo in its toast: the athlete or camera leaves the map until it is assigned again.
  async function unassign(tracker: TrackerRecord) {
    const carrier = tracker.athlete_id ? athletesById.get(tracker.athlete_id)?.name : tracker.stream_id ? camerasById.get(tracker.stream_id)?.name : undefined
    const back = tracker.athlete_id ? { athlete_id: tracker.athlete_id } : tracker.stream_id ? { stream_id: tracker.stream_id } : null
    try {
      await assignTracker(eventId, tracker.id, {})
    } catch (failure) {
      toast({ title: 'Tracker not unassigned', description: apiErrors(failure, 'Try again.'), tone: 'error' })
      return
    }
    toast({
      title: 'Tracker unassigned',
      description: carrier ? `${tracker.external_ref} no longer moves ${carrier}.` : tracker.external_ref,
      tone: 'success',
      action: back
        ? {
            label: 'Undo',
            onClick: () => {
              assignTracker(eventId, tracker.id, back).then(
                () => void load(),
                (failure) => toast({ title: 'Tracker not reassigned', description: apiErrors(failure, 'Try again.'), tone: 'error' }),
              )
            },
          }
        : undefined,
    })
    void load()
  }

  async function remove(athlete: AthleteRecord) {
    setRemovalError('')
    try {
      await removeAthlete(eventId, athlete.id)
    } catch (failure) {
      setRemovalError(apiErrors(failure, 'The athlete couldn’t be removed. Try again.'))
      throw failure
    }
    await load()
  }

  if (!data) {
    return error ? (
      <Notice tone="error" alert action={<Button onClick={() => void load()}>Try again</Button>}>
        {error}
      </Notice>
    ) : (
      <ContentSkeleton label="Loading athletes" variant="list" count={3} />
    )
  }

  const canTrack = data.sources.length > 0

  return (
    <div className="space-y-10">
      {error && (
        <Notice tone="error" alert action={<Button variant="secondary" size="compact" onClick={() => void load()}>Try again</Button>}>
          {error}
        </Notice>
      )}

      {!canTrack && (
        <Notice
          action={
            <ButtonLink to={`/events/${eventId}/settings?tab=tracking`} variant="secondary" size="compact">
              Connect a provider
            </ButtonLink>
          }
        >
          No tracking provider yet. Connect one in settings and athletes with a tracker appear on the event map.
        </Notice>
      )}

      <section className="space-y-4" aria-labelledby="roster-heading">
        <div className="pw-section-heading flex-wrap !mb-0">
          <h2 id="roster-heading">Athletes</h2>
          <div className="flex flex-wrap gap-2">
            <IconButton label="Refresh" variant="ghost" onClick={() => void load()} disabled={refreshing}>
              <RefreshCw />
            </IconButton>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <FileUp aria-hidden="true" />
              Import CSV
            </Button>
            <Button onClick={() => setEditing({ athlete: null })}>
              <Plus aria-hidden="true" />
              Add athlete
            </Button>
          </div>
        </div>

        <Tabs
          label="Athletes and trackers"
          value={view}
          onValueChange={(next) => {
            setView(next)
            setShown(PAGE)
          }}
          actions={
            <TextField
              label={view === 'athletes' ? 'Find an athlete' : 'Find a tracker'}
              hideLabel
              size="compact"
              icon={<Search />}
              placeholder={view === 'athletes' ? 'Name, bib or tracker id' : 'Tracker id, athlete or camera'}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setShown(PAGE)
              }}
            />
          }
          items={[
            {
              value: 'athletes',
              label: 'Athletes',
              count: data.athletes.length,
              content:
                data.athletes.length === 0 ? (
                  <EmptyState
                    icon={<UserRound />}
                    title="No athletes yet"
                    description="Add them one by one, or import your start list. Each shows on the event map once their tracker reports."
                  />
                ) : (
                  <div className="space-y-4 pt-4">
                    <Table caption="Athletes" hideCaption>
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Athlete</TableHeaderCell>
                          <TableHeaderCell type="data">Bib</TableHeaderCell>
                          <TableHeaderCell>Gender</TableHeaderCell>
                          <TableHeaderCell>Race</TableHeaderCell>
                          <TableHeaderCell type="data">Tracker</TableHeaderCell>
                          <TableHeaderCell>Last seen</TableHeaderCell>
                          <TableHeaderCell type="actions">
                            <span className="sr-only">Actions</span>
                          </TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matching.slice(0, shown).map((athlete) => (
                          <TableRow key={athlete.id}>
                            <TableCell>
                              <span className="flex items-center gap-2">
                                <Avatar name={athlete.name} src={athlete.photo_url} size="sm" tone="auto" toneKey={athlete.id} />
                                <span>{athlete.name}</span>
                                {athlete.hidden && (
                                  <Badge tone="quiet" size="compact" icon={<EyeOff />}>
                                    Hidden
                                  </Badge>
                                )}
                              </span>
                            </TableCell>
                            <TableCell type="data" className="whitespace-nowrap">{athlete.bib ?? '—'}</TableCell>
                            <TableCell>{athlete.gender === 'female' ? 'Female' : athlete.gender === 'male' ? 'Male' : '—'}</TableCell>
                            <TableCell>
                              <RaceLabel look={athlete.route_id ? looks.get(athlete.route_id) : undefined} />
                            </TableCell>
                            <TableCell type="data" className="whitespace-nowrap">{athlete.trackers.map((tracker) => tracker.external_ref).join(', ') || '—'}</TableCell>
                            <TableCell className="whitespace-nowrap">{athlete.location ? formatAge(now - Date.parse(athlete.location.recorded_at)) : 'Not yet'}</TableCell>
                            <TableCell type="actions">
                              <AthletePreview athlete={athlete} look={athlete.route_id ? looks.get(athlete.route_id) : undefined} now={now} />
                              <IconButton label={`Edit ${athlete.name}`} variant="ghost" onClick={() => setEditing({ athlete })}>
                                <Pencil />
                              </IconButton>
                              <IconButton
                                label={`Remove ${athlete.name}`}
                                variant="ghost"
                                onClick={() => {
                                  setRemoval(athlete)
                                  setRemovalError('')
                                  setRemovalOpen(true)
                                }}
                              >
                                <Trash2 />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {matching.length === 0 && <p className="text-sm text-neutral-7">No athlete matches “{query.trim()}”.</p>}
                    {matching.length > shown && (
                      <Button variant="secondary" onClick={() => setShown((count) => count + PAGE)}>
                        Show more ({matching.length - shown} left)
                      </Button>
                    )}
                  </div>
                ),
            },
            {
              value: 'trackers',
              label: 'Trackers',
              count: data.trackers.length,
              content:
                data.trackers.length === 0 ? (
                  <EmptyState
                    icon={<RadioTower />}
                    title="No trackers yet"
                    description="A tracker shows here the first time its provider reports it, or when you give an athlete its id."
                  />
                ) : (
                  <div className="space-y-4 pt-4">
                    <TrackersTable
                      trackers={matchingTrackers.slice(0, shown)}
                      athletes={athletesById}
                      cameras={camerasById}
                      now={now}
                      onAssign={assign}
                      onUnassign={(tracker) => void unassign(tracker)}
                    />
                    {matchingTrackers.length === 0 && <p className="text-sm text-neutral-7">No tracker matches “{query.trim()}”.</p>}
                    {matchingTrackers.length > shown && (
                      <Button variant="secondary" onClick={() => setShown((count) => count + PAGE)}>
                        Show more ({matchingTrackers.length - shown} left)
                      </Button>
                    )}
                  </div>
                ),
            },
          ]}
        />
      </section>

      <AthleteDialog
        eventId={eventId}
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        athlete={editing?.athlete ?? null}
        races={data.races}
        canTrack={canTrack}
        onSaved={(athlete) => {
          toast({ title: editing?.athlete ? 'Athlete saved' : 'Athlete added', description: athlete.name, tone: 'success' })
          void load()
        }}
      />
      <AthleteImportDialog
        eventId={eventId}
        open={importOpen}
        onOpenChange={setImportOpen}
        races={data.races}
        canTrack={canTrack}
        onImported={() => void load()}
      />
      <TrackerAssignDialog
        eventId={eventId}
        tracker={assigning?.tracker ?? null}
        currentBib={assigning?.bib}
        open={assigning !== null}
        onOpenChange={(open) => !open && setAssigning(null)}
        cameras={data.cameras}
        onAssigned={() => {
          toast({ title: 'Tracker assigned', tone: 'success' })
          void load()
        }}
      />
      <ConfirmDialog
        open={removalOpen}
        onOpenChange={setRemovalOpen}
        title={`Remove ${removal?.name ?? ''}?`}
        description="They come off your roster and the event map. Their tracker stays, unassigned."
        confirmLabel="Remove athlete"
        onConfirm={() => (removal ? remove(removal) : undefined)}
        failureMessage={removalError || undefined}
      />
    </div>
  )
}
